import { HelpIcon } from '../modules/core/index.js'
import { useDashboardSummary, useTierDistribution, useUnitNames } from '../modules/facilities/index.js'
import { useMe } from '../modules/accounts/index.js'
import { useFacilityBarriers } from '../modules/messages/index.js'
import { useWorkerRoster } from '../modules/roster/index.js'
import { FacilityActivityCard } from '../modules/messages/index.js'
import { BridgeCalloutsCard, MonthHealthCard, MonthPanel, TodayPanel, describePeriod, useSchedulePeriodWorkers, type PeriodWorkerReadiness } from '../modules/schedule/index.js'
import { EngineLog, operatorFacilityActivityApi } from '../modules/messages/index.js'
import { isNotFoundError } from '../modules/core/index.js'
import { FacilityNotConfigured } from '../modules/facilities/index.js'
import type { WorkerTableRow } from '../modules/roster/index.js'
import { useEffect, useMemo, useState } from 'react'
import { LogOut, MessageSquare, Pencil } from 'lucide-react'
import { NewWorkerModal } from '../modules/roster/index.js'
import { EditWorkerModal } from '../modules/roster/index.js'
import { WorkerIcon } from '../modules/core/index.js'
import { RosterHighlights, type RosterMix } from '../modules/facilities/index.js'
import { TIER_COLORS } from '../modules/facilities/index.js'
import { TIER_LABELS } from '../modules/core/index.js'
import { WORKER_ROLE_BADGE_CLASS, WORKER_ROLE_LABELS } from '../modules/core/index.js'
import { EMPLOYMENT_TYPE_BADGE, EMPLOYMENT_TYPE_LABELS } from '../modules/roster/index.js'
import { formatScore } from '../modules/core/index.js'
import { MESSAGES_TAB } from '../modules/messages/index.js'
import { WorkerRowTabs } from '../modules/roster/index.js'
import type { RosterContext } from '../modules/core/index.js'
import { OnTrackHint } from '../modules/scoring/index.js'
import { SortButton, TIER_SORT_RANK, useSort, type SortAccessors } from '../modules/core/index.js'
import { Button } from '../modules/core/index.js'
import { LoadFailure, RefreshButton, Skeleton, useLoadingWindow } from '../modules/core/index.js'

/**
 * Column widths rather than seven equal ones: the worker cell carries two
 * lines (name and id) while score and call-outs are a couple of digits.
 */
const WORKER_TABLE_GRID = 'grid grid-cols-[2.4fr_2fr_0.7fr_0.9fr_1fr_1.5fr] gap-2'

/** An em-dash for a metric the facility has not reported - a 0 here would read as a real measurement. */
function Metric({ value, suffix = '' }: { value: number | null; suffix?: string }) {
  if (value === null) {
    return (
      <span className="text-ink-mid" title="Not reported yet">
        &mdash;
      </span>
    )
  }
  return (
    <span>
      {value}
      {suffix}
    </span>
  )
}

/**
 * Where the worker stands on the month being bid, from the month's readiness (who bids, who saved preferences):
 * the per-tier bid window this column used to read is no longer computed. The old reading stands only until the
 * readiness loads, or where it cannot be read.
 */
function BidStatusCell({ row, readiness }: { row: WorkerTableRow; readiness: PeriodWorkerReadiness | undefined }) {
  if (readiness) {
    if (!readiness.bids) {
      return <span className="text-ink-mid font-bold">Assigned</span>
    }
    if (readiness.preferences) {
      return <span className="text-green font-bold">Saved</span>
    }
    return readiness.reminder === 'preferences_closed' ? <span className="text-ink-mid">Closed · none saved</span> : <span className="text-gold font-bold">Not saved yet</span>
  }
  if (row.bidStatus === 'open') {
    return <span className="text-green font-bold">Bid open</span>
  }
  if (row.bidStatus === 'assigned') {
    return <span className="text-coral font-bold">Assigned</span>
  }
  return (
    <span className="text-ink-mid">
      {row.bidWindowOpensAt ? `Opens ${new Date(row.bidWindowOpensAt).toLocaleDateString()}` : 'Upcoming'}
    </span>
  )
}

type RosterSortKey = 'name' | 'tier' | 'score' | 'callOuts' | 'charting' | 'bidStatus'

/** Not saved first (the ones to chase), then saved, then assigned. */
function bidRank(readiness: PeriodWorkerReadiness | undefined, row: WorkerTableRow): string {
  if (!readiness) {
    return row.bidStatus
  }
  return !readiness.bids ? '3' : readiness.preferences ? '2' : '1'
}

function rosterSort(readiness: ReadonlyMap<string, PeriodWorkerReadiness>): SortAccessors<WorkerTableRow, RosterSortKey> {
  return {
    name: (row) => row.name,
    tier: (row) => TIER_SORT_RANK[row.tier],
    score: (row) => row.score,
    callOuts: (row) => row.callOuts,
    charting: (row) => row.chartingPct,
    bidStatus: (row) => bidRank(readiness.get(row.workerId), row),
  }
}

/** Adds a worker: the app's worker icon and a label, as a button. Above a filled roster, and as the one thing to do about an empty one. */
function AddWorkerButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <Button icon={WorkerIcon} size="sm" className="normal-case tracking-normal" onClick={onClick}>
      {label}
    </Button>
  )
}

/** The element id of a worker's roster row, so Recent Activity can bring it into view. */
function workerRowId(workerId: string): string {
  return `worker-row-${workerId}`
}

interface WorkerTableProps {
  rows: WorkerTableRow[]
  /** Null for a facility administrator, whose roster is read-only. */
  onAddWorker: (() => void) | null
  onEditWorker: ((workerId: string) => void) | null
  /** The one open row, held by the dashboard so an entry in Recent Activity can open its worker. */
  expandedWorkerId: string | null
  onExpand: (workerId: string | null) => void
  /** What the add-ons' roster contributions are told. */
  roster: RosterContext
  /** The client the open row's login is read under. */
  clientId: string | undefined
  /** Per worker, how many of their notes nobody has marked seen: a mark on the closed row, so staff see it without opening anyone. */
  unseenNotesByWorker: ReadonlyMap<string, number>
  /** Opens a worker's row on its Messages tab, from the mark on the row. */
  onOpenMessages: (workerId: string) => void
  /** The latest such request: which row, and a number that changes each time so the row's tabs reopen on Messages even when already open. */
  messagesRequest: { workerId: string; nonce: number } | null
}

function WorkerTable({ rows, onAddWorker, onEditWorker, expandedWorkerId, onExpand, roster, clientId, unseenNotesByWorker, onOpenMessages, messagesRequest }: WorkerTableProps) {
  // One row open at a time: the panel is tall, and two of them open at once
  // pushes the rest of the roster off screen.
  const readinessQuery = useSchedulePeriodWorkers(roster.facilityId, null)
  const readiness = useMemo(() => new Map((readinessQuery.data?.workers ?? []).map((worker) => [worker.workerId, worker])), [readinessQuery.data])
  const sortAccessors = useMemo(() => rosterSort(readiness), [readiness])
  const { sorted, header } = useSort(rows, sortAccessors)
  const bidMonth = readinessQuery.data ? describePeriod(readinessQuery.data.period).split(' ')[0] : null

  if (rows.length === 0) {
    return (
      <div className="border border-marine/15 bg-white px-4 py-6 text-xs text-ink-mid flex flex-col items-start gap-3">
        <p>
          <span className="font-bold text-ink">No workers set up.</span>{' '}
          {onAddWorker ? 'Nobody is on this facility’s roster yet. Add workers one at a time, or ask SeeWhoCares to import a roster file.' : 'Nobody is on this facility’s roster yet. Your operator adds workers.'}
        </p>
        {onAddWorker && <AddWorkerButton onClick={onAddWorker} label="Add the first worker" />}
      </div>
    )
  }

  return (
    <div className="border border-marine/15 overflow-hidden">
      <div className={`${WORKER_TABLE_GRID} bg-marine-dk px-4 py-2 text-[10px] font-bold uppercase tracking-wide text-white/60`}>
        <div>
          <SortButton tone="dark" label="Worker" {...header('name')} />
        </div>
        <div>
          <SortButton tone="dark" label="Tier" {...header('tier')} />
        </div>
        <div>
          <SortButton tone="dark" label="Score" {...header('score')} />
        </div>
        <div>
          <SortButton tone="dark" label="Call-outs" {...header('callOuts')} />
        </div>
        <div>
          <SortButton tone="dark" label="Charting %" {...header('charting')} />
        </div>
        <div>
          <SortButton tone="dark" label={bidMonth ? `${bidMonth} bid` : 'Bid Status'} {...header('bidStatus')} />
        </div>
      </div>
      {sorted.map((row) => {
        const isExpanded = expandedWorkerId === row.workerId
        const panelId = `worker-detail-${row.workerId}`

        return (
          <div key={row.workerId} id={workerRowId(row.workerId)} className="border-b border-marine/15 last:border-b-0 scroll-mt-4">
            <button
              type="button"
              onClick={() => onExpand(isExpanded ? null : row.workerId)}
              aria-expanded={isExpanded}
              aria-controls={panelId}
              className={`${WORKER_TABLE_GRID} w-full text-left px-4 py-2 text-xs items-center border-l-4 transition-colors ${
                isExpanded ? 'border-marine bg-marine-lt' : 'border-transparent hover:bg-marine-lt/40'
              }`}
            >
              <div className="min-w-0 flex items-start gap-2">
                <WorkerIcon className="mt-0.5 w-4 h-4 text-marine" />
                <div className="min-w-0">
                {/* Wraps rather than truncates: a long name's last name must stay on the row. */}
                <div className="font-bold text-ink break-words">{row.name}</div>
                {/* Always shown, not only on a collision: workers may share
                    a name, identity is the id alone, and a duplicate may sit
                    off screen where a conditional hint would never appear. */}
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className={`inline-block px-1.5 py-0.5 text-[10px] font-bold rounded shrink-0 ${WORKER_ROLE_BADGE_CLASS}`} title={WORKER_ROLE_LABELS[row.role]}>
                    {row.role}
                  </span>
                  {EMPLOYMENT_TYPE_BADGE[row.employmentType ?? 'full_time'] && (
                    <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold rounded shrink-0 bg-gold/25 text-ink" title={EMPLOYMENT_TYPE_LABELS[row.employmentType ?? 'full_time']}>
                      {EMPLOYMENT_TYPE_BADGE[row.employmentType ?? 'full_time']}
                    </span>
                  )}
                  {row.leaveDate && (
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-ink-mid/15 px-1.5 py-0.5 text-[10px] font-bold text-ink-mid" title={`Left the facility on ${row.leaveDate}`}>
                      <LogOut aria-hidden="true" className="h-3 w-3" />
                      Left {new Date(`${row.leaveDate}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}
                    </span>
                  )}
                  {(unseenNotesByWorker.get(row.workerId) ?? 0) > 0 && (
                    // Inside the row's own button, so a span acting as one: its click opens the row on Messages and goes no further.
                    <span
                      role="button"
                      tabIndex={0}
                      aria-label={`Open ${row.name}'s messages: ${unseenNotesByWorker.get(row.workerId)} not yet seen`}
                      title="Notes from this worker nobody has marked seen. Open their Messages tab."
                      onClick={(event) => {
                        event.stopPropagation()
                        onOpenMessages(row.workerId)
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          event.stopPropagation()
                          onOpenMessages(row.workerId)
                        }
                      }}
                      className="inline-flex shrink-0 cursor-pointer items-center gap-1 rounded-full bg-coral px-1.5 py-0.5 text-[10px] font-bold text-white hover:brightness-90 focus:outline-none focus:ring-2 focus:ring-coral/50"
                    >
                      <MessageSquare aria-hidden="true" className="h-3 w-3" />
                      {unseenNotesByWorker.get(row.workerId)}
                    </span>
                  )}
                </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="inline-flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${TIER_COLORS[row.tier]}`} />
                  {TIER_LABELS[row.tier]}
                </span>
                <OnTrackHint tier={row.tier} onTrack={row.onTrack} />
              </div>
              <div>{formatScore(row.score)}</div>
              <div>
                <Metric value={row.callOuts} />
              </div>
              <div>
                <Metric value={row.chartingPct} suffix="%" />
              </div>
              <div className="flex items-center justify-between gap-2">
                <BidStatusCell row={row} readiness={readiness.get(row.workerId)} />
                <span className="flex shrink-0 items-center gap-1">
                  {onEditWorker && (
                    // Inside the row's own button, so a span acting as one: its click opens the worker's form and leaves the row as it is.
                    <span
                      role="button"
                      tabIndex={0}
                      aria-label={`Edit ${row.name}`}
                      title="Edit worker"
                      onClick={(event) => {
                        event.stopPropagation()
                        onEditWorker(row.workerId)
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          event.stopPropagation()
                          onEditWorker(row.workerId)
                        }
                      }}
                      className="inline-flex cursor-pointer items-center rounded p-1 text-marine hover:bg-marine/10 hover:text-marine-dk focus:outline-none focus:ring-2 focus:ring-marine/40"
                    >
                      <Pencil aria-hidden="true" strokeWidth={2.5} className="h-4 w-4" />
                    </span>
                  )}
                  <span
                    className={`text-marine/50 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    aria-hidden="true"
                  >
                    ▾
                  </span>
                </span>
              </div>
            </button>

            {/* grid-rows 0fr -> 1fr animates to the panel's natural height,
                which a max-height transition can only approximate. */}
            <div
              id={panelId}
              className={`grid transition-[grid-template-rows] duration-200 ease-out ${
                isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
              }`}
            >
              <div className="overflow-hidden">
                {/* Mounted only while open, so collapsing a row also stops
                    its queries and a reopened row reflects current data. */}
                {/* Details, Scoring, and a tab per add-on with a worker view (src/features/index.ts), each gated by the add-on itself. */}
                {isExpanded && (
                  <div className="bg-marine-lt/40 pt-1">
                    <WorkerRowTabs
                      key={messagesRequest?.workerId === row.workerId ? `messages-${messagesRequest.nonce}` : 'default'}
                      workerId={row.workerId}
                      roster={roster}
                      clientId={clientId}
                      onEdit={onEditWorker ? () => onEditWorker(row.workerId) : null}
                      initialTab={messagesRequest?.workerId === row.workerId ? MESSAGES_TAB : undefined}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export type DashboardTab = 'today' | 'month' | 'roster' | 'activity'

export const DASHBOARD_TABS: readonly DashboardTab[] = ['today', 'month', 'roster', 'activity']

export const DASHBOARD_TAB_LABELS: Record<DashboardTab, string> = {
  today: 'Today',
  month: 'Month',
  roster: 'Roster',
  activity: 'Activity',
}

/** The sub-tab this browser last chose, kept as a convenience; a blocked or empty store opens on Overview. */
export const DASHBOARD_TAB_STORAGE_KEY = 'autonome.dashboard.tab'

interface DashboardProps {
  facilityId: string
  /** The facility's display name, for the New worker form. */
  facilityName: string
  /** The signed-in client, which the worker form posts under; undefined while unknown. */
  clientId: string | undefined
  /** True for an operator session: the roster context's role, which an add-on's advice reads. */
  canManageRoster: boolean
  /** True for an operator or a facility administrator at their own facility: both edit a worker and manage their login. */
  canEditWorkers: boolean
  /** Where this facility's Configuration is, for an add-on's advice about its switch. */
  configHref: string
  /** Where this facility's Tools are, for the Month card's buttons. */
  toolsHref: string
  /** The section showing, which the shell's main navigation chooses (the mockup's flat bar: Today, Month, Roster, Activity). */
  current: DashboardTab
  choose: (tab: DashboardTab) => void
}

/**
 * The facility dashboard, the facility administrator's one place to run the day and the month (facility operations
 * dashboard plan, 24 October 2026; sub-tabbed since 22 October): Today (what needs a person, the census, coverage, the
 * open seats, the Clinical Bridge callouts and what the engine did - or the set-up list for a facility with nothing to
 * run), Month (the facility in bold strokes and the month's health), Roster and Activity. Today wears the count of what
 * needs a person and the Roster the notes nobody has marked seen. Every section stays mounted, so an open row or a
 * half-filled form survives a switch, and a worker's name in Activity or on a Month finding opens their roster row.
 */
export function Dashboard({ facilityId, facilityName, clientId, canManageRoster, canEditWorkers, configHref, toolsHref, current, choose }: DashboardProps) {
  const [isAddingWorker, setIsAddingWorker] = useState(false)
  const [editingWorkerId, setEditingWorkerId] = useState<string | null>(null)
  const [expandedWorkerId, setExpandedWorkerId] = useState<string | null>(null)
  const [messagesRequest, setMessagesRequest] = useState<{ workerId: string; nonce: number } | null>(null)
  // A row asked for from another tab: brought into view once the Roster tab is showing, which is a render later.
  const [rowToShow, setRowToShow] = useState<string | null>(null)
  useEffect(() => {
    if (current === 'roster' && rowToShow !== null) {
      document.getElementById(workerRowId(rowToShow))?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      setRowToShow(null)
    }
  }, [current, rowToShow])
  /** Opening or closing a row by hand forgets any earlier request, so the row opens on Scoring as usual. */
  const expandRow = (workerId: string | null): void => {
    setMessagesRequest(null)
    setExpandedWorkerId(workerId)
  }
  const openMessages = (workerId: string): void => {
    setMessagesRequest((current) => ({ workerId, nonce: (current?.nonce ?? 0) + 1 }))
    setExpandedWorkerId(workerId)
  }
  const activityApi = useMemo(() => operatorFacilityActivityApi(facilityId), [facilityId])
  // Whether the client is a demo: the Today tab's two-minute demo plays there alone (open shift lifecycle plan D25).
  const me = useMe()
  // Notes nobody has marked seen: counted per worker for a mark on each row, and across the roster for the Roster tab's badge and the activity card's line.
  const barriersQuery = useFacilityBarriers(facilityId)
  const facilityBarriers = barriersQuery.data
  const unseenNotesByWorker = useMemo(() => {
    const counts = new Map<string, number>()
    for (const barrier of facilityBarriers ?? []) {
      if (barrier.acknowledgement === null) {
        counts.set(barrier.workerId, (counts.get(barrier.workerId) ?? 0) + 1)
      }
    }
    return counts
  }, [facilityBarriers])
  const unseenNotes = [...unseenNotesByWorker.values()].reduce((total, count) => total + count, 0)
  const summaryQuery = useDashboardSummary(facilityId)
  const tierQuery = useTierDistribution(facilityId)
  const workersQuery = useWorkerRoster(facilityId)
  // Above the early returns below: a hook must run on every render.
  const workerRows = workersQuery.data
  const workerNames = useMemo(() => new Map((workerRows ?? []).map((worker) => [worker.workerId, worker.name])), [workerRows])
  const unitNames = useUnitNames(facilityId)
  // The reads every tab waits on; each tab adds its own to the same window (the loading standard).
  useLoadingWindow({
    shown: true,
    visit: `dashboard:${facilityId}`,
    title: `Opening ${current === 'today' ? 'Today' : `the ${DASHBOARD_TAB_LABELS[current].toLowerCase()}`}`,
    subtitle: facilityName,
    sources: [
      { key: 'dashboard-roster', label: 'The roster', query: workersQuery },
      { key: 'dashboard-summary', label: 'This month’s totals', query: summaryQuery },
      { key: 'dashboard-tiers', label: 'The tiers', query: tierQuery },
      { key: 'dashboard-notes', label: 'Notes from workers', query: barriersQuery },
    ],
  })

  if (summaryQuery.isPending || tierQuery.isPending || workersQuery.isPending) {
    return <Skeleton lines={6} label="Loading the dashboard" className="p-8" />
  }

  if (isNotFoundError(summaryQuery.error) || isNotFoundError(tierQuery.error) || isNotFoundError(workersQuery.error)) {
    return <FacilityNotConfigured facilityId={facilityId} />
  }

  if (summaryQuery.isError || tierQuery.isError || workersQuery.isError) {
    return (
      <LoadFailure className="p-8" message="Failed to load dashboard data. Confirm VITE_API_BASE_URL is set and the API is reachable." onRetry={() => void Promise.all([summaryQuery.refetch(), tierQuery.refetch(), workersQuery.refetch()])} retrying={summaryQuery.isFetching || tierQuery.isFetching || workersQuery.isFetching} />
    )
  }

  const summary = summaryQuery.data
  const tiers = tierQuery.data
  const workers = workersQuery.data
  const hasWorkers = workers.length > 0
  // Adding a worker is open to both an operator and a facility administrator (whose reach is this facility); editing one stays the operator's.
  const openAddWorker = (): void => setIsAddingWorker(true)
  const openEditWorker = canEditWorkers ? (workerId: string) => setEditingWorkerId(workerId) : null
  // The roster is the operator's to manage; anyone else here is a facility administrator.
  const roster: RosterContext = { facilityId, role: canManageRoster ? 'operator' : 'facility_administrator', addOnsHref: configHref }
  // This dashboard on its Month tab: where the Today tab's month-wide decisions and set-up steps send the person.
  const dashboardHref = toolsHref.replace(/\/tools$/, '') || '/'
  const monthHref = `${dashboardHref}?tab=month`

  /** Opens a worker's roster row from another tab and brings it into view; someone no longer on the roster has no row to open. */
  const openWorkerRow = (workerId: string) => {
    if (!workerNames.has(workerId)) {
      return
    }
    expandRow(workerId)
    choose('roster')
    setRowToShow(workerId)
  }

  const today = new Date().toISOString().slice(0, 10)
  const mix: RosterMix = {
    fullTime: workers.filter((row) => (row.employmentType ?? 'full_time') === 'full_time').length,
    partTime: workers.filter((row) => row.employmentType === 'part_time').length,
    prn: workers.filter((row) => row.employmentType === 'prn').length,
    agency: workers.filter((row) => row.employmentType === 'agency').length,
    leaving: workers.filter((row) => row.leaveDate != null && row.leaveDate >= today).length,
  }
  const onTrackForGold = workers.filter((row) => row.tier !== 'gold' && row.onTrack?.tier === 'gold').length


  return (
    <div className="max-w-5xl mx-auto p-6 flex flex-col gap-6">

      {/* The day (facility operations dashboard plan §3): what needs a person, the census, coverage, the open seats, the
          Clinical Bridge callouts and what the engine did - or, for a facility with nothing to run yet, its set-up list. */}
      <div hidden={current !== 'today'}>
        <TodayPanel
          shown={current === 'today'}
          facilityId={facilityId}
          role={canManageRoster ? 'operator' : 'facility_administrator'}
          rosterCount={workers.length}
          names={workerNames}
          configHref={configHref}
          monthHref={monthHref}
          onAddWorker={openAddWorker}
          engineLog={<EngineLog api={activityApi} names={workerNames} units={unitNames} onOpenActivity={() => choose('activity')} />}
          bridge={<BridgeCalloutsCard facilityId={facilityId} role={roster.role} />}
          isDemo={me.data?.isDemo === true}
        />
      </div>

      {/* The month being built (plan §3): how much the roster fills, then its health, build and release, the board after it,
          census planning and the shifts it publishes - the sections that were the Tools tab's. */}
      <div hidden={current !== 'month'}>
        <MonthPanel
          shown={current === 'month'}
          facilityId={facilityId}
          dashboardHref={dashboardHref}
          onAddWorker={openAddWorker}
          role={canManageRoster ? 'operator' : 'facility_administrator'}
          hasWorkers={hasWorkers}
          configHref={configHref}
          overview={
            // The layout classes sit on an inner element: a `flex` class on a hidden one would show it again.
            <div className="flex flex-col gap-8">
              {/* Every finding with the button that fixes it. */}
              <MonthHealthCard facilityId={facilityId} dashboardHref={dashboardHref} configHref={configHref} onAddWorker={openAddWorker} onEditWorker={openWorkerRow} names={workerNames} />
            </div>
          }
        />
      </div>

      <div hidden={current !== 'activity'}>
        <FacilityActivityCard
          api={activityApi}
          names={workerNames}
          units={unitNames}
          onSelectWorker={openWorkerRow}
          unseenNotes={{
            count: unseenNotes,
            link: (
              <button type="button" onClick={() => choose('roster')} className="font-bold text-marine underline decoration-marine/40 underline-offset-2 hover:text-marine-dk">
                Open the Roster tab and a worker’s Messages tab to read them.
              </button>
            ),
          }}
        />
      </div>

      <div hidden={current !== 'roster'}>
        {/* The roster in bold strokes before the table (mockup screen 8): the figures "This facility at a glance" had alone. */}
        <div className="mb-6">
          <RosterHighlights summary={summary} tiers={tiers.tiers} mix={mix} onTrackForGold={onTrackForGold} unseenNotes={unseenNotes} />
        </div>
        <div className="flex items-end justify-between gap-4 text-[10px] font-bold uppercase tracking-wide text-marine border-b border-marine/15 pb-2 mb-3">
          <span className="inline-flex items-center gap-1">
            Worker Score Table — Live View
            <HelpIcon topic="roster.dashboard" />
          </span>
          <span className="inline-flex items-center gap-2 normal-case tracking-normal">
            <RefreshButton
              size="sm"
              onRefresh={() => Promise.all([workersQuery.refetch(), summaryQuery.refetch(), tierQuery.refetch(), barriersQuery.refetch()])}
              refreshing={workersQuery.isFetching || barriersQuery.isFetching}
            />
            {hasWorkers && <AddWorkerButton onClick={openAddWorker} label="New worker" />}
          </span>
        </div>
        <WorkerTable rows={workers} onAddWorker={openAddWorker} onEditWorker={openEditWorker} expandedWorkerId={expandedWorkerId} onExpand={expandRow} roster={roster} clientId={clientId} unseenNotesByWorker={unseenNotesByWorker} onOpenMessages={openMessages} messagesRequest={messagesRequest} />
      </div>

      {isAddingWorker && <NewWorkerModal clientId={clientId} facility={{ facilityId, name: facilityName }} onClose={() => setIsAddingWorker(false)} />}
      {editingWorkerId !== null && <EditWorkerModal clientId={clientId} facility={{ facilityId, name: facilityName }} workerId={editingWorkerId} onClose={() => setEditingWorkerId(null)} />}
    </div>
  )
}
